import { AlertTriangle, Loader2 } from 'lucide-react'
import { Modal } from '../../ui/Modal'
import { useCurrency } from '../../../hooks/useCurrency'

interface Props {
  open: boolean
  monthLabel: string
  periodCharge: number
  totalTaxCharge: number
  posting: boolean
  errorMessage: string | null
  onCancel: () => void
  onConfirm: () => void
}

export function CitConfirmationModal({
  open, monthLabel, periodCharge, totalTaxCharge,
  posting, errorMessage, onCancel, onConfirm,
}: Props) {
  const { format } = useCurrency()

  return (
    <Modal
      open={open}
      onClose={posting ? () => {} : onCancel}
      title={`Post CIT accrual for ${monthLabel}?`}
      size="lg"
    >
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
          <Row label="Period" value={monthLabel} />
          <Row label="This period's charge" value={format(periodCharge)} bold />
          <Row label="Total YTD tax provision" value={format(totalTaxCharge)} />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">This action is final.</span>{' '}
            A journal entry will be posted atomically to the GL. Once posted,
            the accrual is immutable — corrections happen through the next
            period&rsquo;s accrual.
          </p>
        </div>

        {errorMessage && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            disabled={posting}
            className="btn-secondary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={posting}
            onClick={onConfirm}
            className="btn-primary disabled:opacity-50"
          >
            {posting && <Loader2 className="h-4 w-4 animate-spin" />}
            {posting ? 'Posting…' : 'Yes, Post Accrual'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-slate-700">{label}</p>
      <p className={`tabular-nums whitespace-nowrap ${
        bold ? 'text-sm font-semibold text-slate-900' : 'text-sm text-slate-800'
      }`}>
        {value}
      </p>
    </div>
  )
}
